import React from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Badge,
  Button,
  useColorModeValue,
} from '@chakra-ui/react';
import { formatDistanceToNow } from 'date-fns';
import { getPriorityColor, getNotificationTypeDisplay } from '../../utils/notificationUtils';
import type { Notification } from '../../types/notification';

interface NotificationItemProps {
  notification: Notification;
  onClick: () => void;
}

const NotificationItem: React.FC<NotificationItemProps> = ({
  notification,
  onClick,
}) => {
  const bgColor = useColorModeValue('white', 'gray.800');
  const hoverBgColor = useColorModeValue('gray.50', 'gray.700');
  const unreadBgColor = useColorModeValue('blue.50', 'blue.900');
  const readTextColor = useColorModeValue('gray.500', 'gray.400');

  return (
    <Box
      p={4}
      bg={notification.isRead ? bgColor : unreadBgColor}
      cursor="pointer"
      onClick={onClick}
      position="relative"
      _hover={{
        bg: hoverBgColor,
      }}
      transition="background-color 0.2s"
    >
      <VStack align="flex-start" spacing={2}>
        {/* Header with title and badges */}
        <HStack justify="space-between" align="flex-start" w="full">
          <VStack align="flex-start" spacing={1} flex={1}>
            <Text
              fontWeight={notification.isRead ? "normal" : "bold"}
              fontSize="sm"
              color={notification.isRead ? readTextColor : undefined}
              noOfLines={2}
            >
              {notification.title}
            </Text>
            
            <HStack spacing={2}>
              <Badge
                colorScheme={getPriorityColor(notification.priority)}
                size="sm"
                variant="subtle"
              >
                {notification.priority.toUpperCase()}
              </Badge>
              <Badge variant="outline" size="sm">
                {getNotificationTypeDisplay(notification.type)}
              </Badge>
            </HStack>
          </VStack>
          <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); onClick(); }}>
            View
          </Button>
        </HStack>
        
        {/* Message */}
        <Text
          fontSize="xs"
          color={notification.isRead ? readTextColor : "gray.600"}
          noOfLines={3}
        >
          {notification.message}
        </Text>
        
        {/* Footer with timestamp */}
        <HStack justify="space-between" align="center" w="full">
          <Text fontSize="xs" color="gray.500">
            {formatDistanceToNow(notification.created_at, { addSuffix: true })}
          </Text>
          
          {notification.read_at && (
            <Text fontSize="xs" color="gray.400">
              Read {formatDistanceToNow(notification.read_at, { addSuffix: true })}
            </Text>
          )}
        </HStack>
      </VStack>
      
      {/* Unread indicator dot */}
      {!notification.isRead && (
        <Box
          position="absolute"
          left={2}
          top="50%"
          transform="translateY(-50%)"
          w={2}
          h={2}
          bg="blue.500"
          borderRadius="full"
        />
      )}
    </Box>
  );
};

export default NotificationItem;
